import React, { useState, useEffect } from "react";
import { 
  Users, 
  UserRound, 
  Building2, 
  Activity, 
  TrendingUp, 
  Calendar,
  ShieldAlert
} from "lucide-react";
import AdminSidebar from "./AdminSidebar";
import { db } from "../../firebase/config";
import { ref, onValue } from "firebase/database";
import "./admin.css";

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalPatients: 0,
    totalDoctors: 0,
    totalHospitals: 0,
    pendingHospitals: 0
  });

  const [recentUsers, setRecentUsers] = useState([]);

  useEffect(() => {
    const usersRef = ref(db, "users");
    const hospitalsRef = ref(db, "hospitals");

    // Listen to users for census stats & recent activity
    const unsubscribeUsers = onValue(usersRef, (snapshot) => {
      try {
        const data = snapshot.val() || {};
        const usersList = Object.values(data);
        
        const patients = usersList.filter(u => u.role?.toLowerCase() === "patient").length;
        const doctors = usersList.filter(u => u.role?.toLowerCase() === "doctor").length;
        
        setStats(prev => ({ ...prev, totalPatients: patients, totalDoctors: doctors }));
        
        // Identity Feed: last 5 clinical authorizations
        const sorted = Object.entries(data)
          .map(([uid, val]) => ({ uid, ...val }))
          .filter(u => u.name && u.createdAt) // Ensure record integrity
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5);
        setRecentUsers(sorted);
      } catch (error) {
        console.error("IDENTITY_SYNC_ERROR:", error);
      }
    }, (error) => {
      console.error("PERMISSION_DENIED: User lacks OVERSIGHT authorization.", error);
    });

    // Listen to hospitals for partner network stats
    const unsubscribeHospitals = onValue(hospitalsRef, (snapshot) => {
      try {
        const data = snapshot.val() || {};
        const list = Object.values(data);
        const total = list.length;
        const pending = list.filter(h => h.status === "pending").length;
        setStats(prev => ({ ...prev, totalHospitals: total, pendingHospitals: pending }));
      } catch (error) {
        console.error("FACILITY_SYNC_ERROR:", error);
      }
    }, (error) => {
      console.error("PERMISSION_DENIED: Facility node restricted.", error);
    });

    return () => {
      unsubscribeUsers();
      unsubscribeHospitals();
    };
  }, [db]);

  return (
    <div className="admin-shell">
      <AdminSidebar />
      
      <main className="admin-content">
        <header className="admin-header">
          <div className="header-info">
            <h1>SYS_OVERVIEW</h1>
            <p>OVERSIGHT_NODE: CMD_CENTER_v4.2</p>
          </div>
          <div className="system-status">
            <Activity size={14} className="icon-blue pulse" strokeWidth={3} />
            <span>DB_SYNC: ACTIVE_READY</span>
          </div>
        </header>

        <section className="stats-grid">
          <div className="stat-card glass-card">
            <div className="stat-icon-wrapper">
              <UserRound size={20} className="icon-blue" strokeWidth={2.5} />
            </div>
            <div className="stat-data">
              <span className="stat-label">PATIENT_CENSUS</span>
              <h2 className="stat-value">{stats.totalPatients.toString().padStart(3, '0')}</h2>
            </div>
          </div>

          <div className="stat-card glass-card">
            <div className="stat-icon-wrapper">
              <Users size={20} className="icon-green" strokeWidth={2.5} />
            </div>
            <div className="stat-data">
              <span className="stat-label">STAFF_REGISTRY</span>
              <h2 className="stat-value">{stats.totalDoctors.toString().padStart(3, '0')}</h2>
            </div>
          </div>

          <div className="stat-card glass-card">
            <div className="stat-icon-wrapper">
              <Building2 size={20} className="icon-purple" strokeWidth={2.5} />
            </div>
            <div className="stat-data">
              <span className="stat-label">PARTNER_HOSPITALS</span>
              <h2 className="stat-value">{stats.totalHospitals.toString().padStart(3, '0')}</h2>
            </div>
          </div>

          <div className="stat-card glass-card">
            <div className="stat-icon-wrapper" style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
              <ShieldAlert size={20} style={{ color: '#f59e0b' }} strokeWidth={2.5} />
            </div>
            <div className="stat-data">
              <span className="stat-label">PENDING_AUTHORIZATIONS</span>
              <h2 className="stat-value" style={{ color: '#f59e0b' }}>{stats.pendingHospitals.toString().padStart(3, '0')}</h2>
            </div>
          </div>
        </section>

        <div className="admin-main-grid">
          {/* Recent Registrations HUD */}
          <section className="registry-list-section glass-card">
            <div className="section-header">
              <h3>RECENT_REGISTRATIONS_FEED</h3>
              <TrendingUp size={16} className="icon-blue" />
            </div>
            <div className="registration-list" style={{ padding: '1rem' }}>
              {recentUsers.map((user) => (
                <div key={user.uid} className="reg-item" style={{ border: '1px solid rgba(59, 130, 246, 0.05)', borderRadius: '4px' }}>
                  <div className="reg-avatar">
                    {user.name.charAt(0)}
                  </div>
                  <div className="reg-info">
                    <span className="reg-name" style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem' }}>{user.name}</span>
                    <span className="reg-email" style={{ fontSize: '0.7rem', color: 'var(--admin-accent-blue)', opacity: 0.7 }}>{user.uid.slice(0, 16)}...</span>
                  </div>
                  <div className="reg-meta" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                    <span className={`status-badge ${user.role}`}>{user.role}</span>
                    <span className="reg-date" style={{ fontSize: '0.65rem', color: 'var(--admin-text-dim)', fontFamily: 'Space Mono, monospace' }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* System Security HUD */}
          <section className="system-alerts glass-card" style={{ padding: '2rem' }}>
            <div className="section-header" style={{ padding: '0 0 1.5rem 0', borderBottom: '1px solid rgba(59, 130, 246, 0.1)' }}>
              <h3>SECURITY_OVERSIGHT</h3>
              <ShieldAlert size={18} className="icon-red pulse" />
            </div>
            <div className="alerts-body" style={{ marginTop: '1.5rem' }}>
              <div className="uptime-meter" style={{ background: 'rgba(59, 130, 246, 0.03)', padding: '1.5rem', borderRadius: '4px', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', fontFamily: 'Space Mono, monospace', fontSize: '0.7rem' }}>
                  <span>DB_SYNC_UPTIME</span>
                  <span style={{ color: 'var(--admin-success)' }}>99.99%</span>
                </div>
                <div style={{ height: '4px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ width: '99.99%', height: '100%', background: 'var(--admin-success)', boxShadow: '0 0 10px var(--admin-success)' }}></div>
                </div>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--admin-text-dim)', marginTop: '1.5rem', lineHeight: '1.6', fontFamily: 'Space Mono, monospace' }}>
                &gt; MONITORING_NODE: SECURE<br/>
                &gt; NO_CRITICAL_BREACHES_DETECTED<br/>
                &gt; FIREBASE_REALTIME: CONNECTED
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
